// Small color utility - only what the palette customization needs.
// All inputs/outputs are #RRGGBB hex strings unless noted. Ported
// verbatim from workshop's workshop/utils/color.js, typed.

export function hexToRgb(hex: string): [number, number, number] {
  let s = hex.replace(/^#/, "");
  if (s.length === 3) s = s.split("").map((c) => c + c).join("");
  const n = Number.parseInt(s, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return "#" + c(r) + c(g) + c(b);
}

export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [h, s, l];
}

export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 0.5) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    hue2rgb(p, q, h + 1 / 3) * 255,
    hue2rgb(p, q, h) * 255,
    hue2rgb(p, q, h - 1 / 3) * 255,
  ];
}

export function darken(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  const [h, s, l] = rgbToHsl(r, g, b);
  const newL = Math.max(0, Math.min(1, l - amount));
  const [nr, ng, nb] = hslToRgb(h, s, newL);
  return rgbToHex(nr, ng, nb);
}

export function lighten(hex: string, amount: number): string {
  return darken(hex, -amount);
}

export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  const srgb = (c: number): number => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * srgb(r) + 0.7152 * srgb(g) + 0.0722 * srgb(b);
}

// Pick a readable foreground (black or white) for a given background.
export function pickInk(bgHex: string, darkInk = "#000000", lightInk = "#ffffff"): string {
  return luminance(bgHex) > 0.55 ? darkInk : lightInk;
}

export function soften(hex: string, amount = 0.05): string {
  return luminance(hex) > 0.55 ? darken(hex, amount) : lighten(hex, amount);
}

// Linearly blend two hex colours. ratio=0 → a, ratio=1 → b.
export function mixHex(a: string, b: string, ratio = 0.5): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const k = Math.max(0, Math.min(1, ratio));
  return rgbToHex(ar * (1 - k) + br * k, ag * (1 - k) + bg * k, ab * (1 - k) + bb * k);
}
